import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { CircularProgress } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'

interface ProjectBuilderAgentProps {
  onAnalyze: (
    projectName: string,
    description: string,
    startDate: string,
    endDate: string,
  ) => Promise<void>
  isLoading: boolean
}

export const ProjectBuilderAgent = observer(({
  onAnalyze,
  isLoading,
}: ProjectBuilderAgentProps) => {
  const { translation } = useLocalizationStore()
  const notificationStore = useNotificationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder
  const [projectName, setProjectName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [description, setDescription] = useState('')

  const handleSubmit = async () => {
    if (!projectName.trim() || !description.trim() || isLoading) {
      return
    }

    // Only validate end date is after start date if both have been entered by user
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      notificationStore.enqueueError(projectBuilderTranslation.endDateValidation())
      
      return
    }

    await onAnalyze(projectName, description, startDate, endDate)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <AppPaper sx={styles.mainContent}>
      <AppBox sx={styles.inputContainer}>
        <AppTextField
          label={projectBuilderTranslation.projectName()}
          m={0}
          value={projectName}
          placeholder={projectBuilderTranslation.inputProjectName()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
          fullWidth
          required
        />

        <AppBox sx={styles.dateSection}>
          <AppTextField
            label={projectBuilderTranslation.startDate()}
            m={0}
            type="date"
            value={startDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <AppTextField
            label={projectBuilderTranslation.endDate()}
            m={0}
            type="date"
            value={endDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
            fullWidth
            required
          />
        </AppBox>

        <AppTextField
          label={projectBuilderTranslation.projectDescription()}
          m={0}
          multiline
          rows={6}
          fullWidth
          placeholder={projectBuilderTranslation.inputPlaceholder()}
          value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />

        <AppBox sx={styles.buttonContainer}>
          <AppButton
            fontSize={14}
            variant='contained'
            onClick={handleSubmit}
            disabled={!projectName.trim() || !description.trim() || isLoading}
            textValue={projectBuilderTranslation.generateButton()}
            endIcon={ isLoading ? <CircularProgress size={16} color='inherit' sx={{ margin: '0px 3px' }} /> : null }
          />
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  mainContent: {
    width: '100%',
    borderRadius: '12px',
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    border: 0,
  },
  inputContainer: {
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  dateSection: {
    display: 'flex',
    gap: 20,
    width: '100%',
  },
  buttonContainer: {
    justifyContent: 'flex-end',
  },
}